import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { WhatsAppCTA } from "@/components/WhatsApp";
import { SITE } from "@/config/site";
import { Inbox, Mail, Phone, MessageCircle, CheckCircle2, Archive, Clock, ChevronRight } from "lucide-react";

/**
 * Contact-form inquiries inbox for the clinic team.
 *
 * Every public "Contact us" submission fans out a `contact_inquiry`
 * notification to all active staff — this page is where they triage the queue.
 * Any clinician or admin can update status, add a note, or reassign.
 */

const STATUS_ORDER = ["new", "in_progress", "resolved", "archived"];
const STATUS_LABEL = { new: "New", in_progress: "In progress", resolved: "Resolved", archived: "Archived" };
const STATUS_STYLE = {
  new: { bg: "#f47a2022", fg: "#FF7A45", border: "#f47a2055" },
  in_progress: { bg: "#F59E0B22", fg: "#FBBF24", border: "#F59E0B55" },
  resolved: { bg: "#22C55E22", fg: "#4ADE80", border: "#22C55E55" },
  archived: { bg: "#71717A22", fg: "#A1A1AA", border: "#71717A55" },
};

function StatusChip({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.new;
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded"
      style={{ background: s.bg, color: s.fg, border: `1px solid ${s.border}` }}
      data-testid={`inq-status-${status}`}
    >
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function InquiryRow({ inq, selected, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(inq)}
      className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/[0.03] transition-colors ${selected ? "bg-white/[0.04]" : ""}`}
      data-testid={`inquiry-row-${inq.id}`}
    >
      <div className="flex items-center gap-2 justify-between">
        <div className="fl-heading text-sm truncate">{inq.name}</div>
        <StatusChip status={inq.status || "new"} />
      </div>
      <div className="text-[11px] mt-0.5" style={{ color: "var(--fl-muted-2)" }}>
        {inq.email} · {inq.topic || "general"} · {formatWhen(inq.created_at)}
      </div>
      <div className="text-xs mt-1 line-clamp-2" style={{ color: "var(--fl-muted)" }}>{inq.message}</div>
    </button>
  );
}

function InquiryDetail({ inq, onPatch }) {
  const [note, setNote] = useState(inq.staff_note || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setNote(inq.staff_note || ""); }, [inq.id]);

  const mailto = useMemo(() => {
    const subject = `Re: your enquiry to Functional LAB (${inq.topic || "general"})`;
    const body = `Hi ${inq.name.split(" ")[0]},\n\nThanks for getting in touch with Functional LAB. `;
    return `mailto:${inq.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [inq]);

  const waMsg = useMemo(
    () => `Hi ${inq.name.split(" ")[0]}, this is Functional LAB following up on your enquiry (${inq.topic || "general"}). We received your message and would love to help.`,
    [inq]
  );

  const save = async (updates) => {
    setSaving(true);
    try {
      await api.patch(`/contact/inquiries/${inq.id}`, updates);
      onPatch({ ...inq, ...updates });
      toast.success("Inquiry updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  // Fire-and-forget: log a reply entry when the clinician clicks a channel CTA.
  // The click still hits the mailto:/tel:/WhatsApp handler as normal — this
  // just records the touch on the server-side timeline.
  const logReply = async (channel, snippet) => {
    try {
      const { data } = await api.post(`/contact/inquiries/${inq.id}/reply`, { channel, snippet });
      const replies = [...(inq.replies || []), data.entry];
      onPatch({ ...inq, replies, status: data.status });
    } catch (e) {
      // Silently swallow — the click still opened the mail/WA client.
      console.error("Failed to log reply", e);
    }
  };

  const timeline = (inq.replies || []).slice().reverse();

  return (
    <div className="p-6" data-testid={`inquiry-detail-${inq.id}`}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="fl-label" style={{ color: "var(--fl-muted-2)" }}>Enquiry · {inq.topic || "general"} · {inq.source || "contact_page"}</div>
          <h2 className="fl-heading text-2xl mt-1">{inq.name}</h2>
          <div className="text-xs mt-1" style={{ color: "var(--fl-muted)" }}>Received {formatWhen(inq.created_at)}</div>
        </div>
        <StatusChip status={inq.status || "new"} />
      </div>

      <div className="grid sm:grid-cols-2 gap-2 mb-4">
        <a
          href={mailto}
          onClick={() => logReply("email", `mailto:${inq.email}`)}
          className="fl-btn-ghost text-sm px-3 py-2 rounded-md inline-flex items-center gap-2 justify-center"
          data-testid="inq-email-cta"
        >
          <Mail size={14} /> Email {inq.email}
        </a>
        {inq.phone ? (
          <a
            href={`tel:${inq.phone.replace(/\s/g,'')}`}
            onClick={() => logReply("phone", inq.phone)}
            className="fl-btn-ghost text-sm px-3 py-2 rounded-md inline-flex items-center gap-2 justify-center"
            data-testid="inq-call-cta"
          >
            <Phone size={14} /> Call {inq.phone}
          </a>
        ) : (
          <div className="text-xs px-3 py-2 rounded-md border border-white/10 inline-flex items-center gap-2 justify-center" style={{ color: "var(--fl-muted-2)" }}>
            <Phone size={14} /> No phone provided
          </div>
        )}
        <WhatsAppCTA
          message={waMsg}
          variant="soft"
          size="sm"
          fullWidth
          testid="inq-wa-cta"
          onClick={() => logReply("whatsapp", waMsg.slice(0, 180))}
        >
          Reply on WhatsApp ({SITE.whatsapp || "clinic"})
        </WhatsAppCTA>
      </div>

      <div className="fl-card p-4 mb-4">
        <div className="fl-label mb-1" style={{ color: "var(--fl-muted-2)" }}>Message</div>
        <div className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: "var(--fl-text)" }}>{inq.message}</div>
      </div>

      {/* Reply timeline — auto-populated when clinicians click through to
          their mail app / WhatsApp / phone. Newest first. */}
      <div className="fl-card p-4 mb-4" data-testid="inq-timeline">
        <div className="fl-label mb-2 flex items-center gap-2" style={{ color: "var(--fl-muted-2)" }}>
          <Clock size={12} /> Reply history ({timeline.length})
        </div>
        {timeline.length === 0 ? (
          <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>No replies logged yet. Every Email / Call / WhatsApp click will be timestamped here.</div>
        ) : (
          <ul className="space-y-2">
            {timeline.map((r) => {
              const Icon = r.channel === "email" ? Mail : r.channel === "whatsapp" ? MessageCircle : r.channel === "phone" ? Phone : ChevronRight;
              return (
                <li key={r.id} className="flex items-start gap-3 text-xs" data-testid={`inq-reply-${r.id}`}>
                  <div className="mt-0.5 w-6 h-6 rounded-full grid place-items-center" style={{ background: "var(--fl-card-bg, #1a1a1a)", border: "1px solid var(--fl-border, #303030)" }}>
                    <Icon size={12} className="text-[color:var(--fl-orange)]" />
                  </div>
                  <div className="flex-1">
                    <div style={{ color: "var(--fl-text)" }}>
                      <b>{r.staff_name}</b> replied via <b style={{ color: "var(--fl-orange)" }}>{r.channel}</b>
                    </div>
                    <div style={{ color: "var(--fl-muted-2)" }}>{formatWhen(r.at)}{r.snippet ? ` · ${r.snippet.slice(0, 80)}${r.snippet.length > 80 ? "…" : ""}` : ""}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="fl-card p-4 mb-4">
        <div className="fl-label mb-1" style={{ color: "var(--fl-muted-2)" }}>Internal note (visible to staff only)</div>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          className="fl-input w-full text-sm px-3 py-2 rounded-md resize-y"
          data-testid="inq-note"
        />
        <button
          type="button"
          onClick={() => save({ note })}
          disabled={saving || note === (inq.staff_note || "")}
          className="fl-btn-ghost text-xs mt-2 px-3 py-1.5 rounded"
          data-testid="inq-save-note"
        >
          {saving ? "Saving…" : "Save note"}
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_ORDER.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => save({ status: s })}
            disabled={saving || inq.status === s}
            className={`text-xs px-3 py-1.5 rounded border ${inq.status === s ? "opacity-40" : "hover:border-[color:var(--fl-orange)]"}`}
            style={{ borderColor: STATUS_STYLE[s].border, color: STATUS_STYLE[s].fg }}
            data-testid={`inq-set-${s}`}
          >
            {s === "resolved" ? <CheckCircle2 size={12} className="inline mr-1" /> : s === "archived" ? <Archive size={12} className="inline mr-1" /> : s === "in_progress" ? <Clock size={12} className="inline mr-1" /> : null}
            Mark as {STATUS_LABEL[s]}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ClinicianInquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contact/inquiries");
      setItems(data);
      const openParam = searchParams.get("open");
      if (openParam && data.find((d) => d.id === openParam)) setSelectedId(openParam);
      else if (!selectedId && data.length) setSelectedId(data[0].id);
    } catch (e) {
      toast.error("Could not load inquiries");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    if (!statusFilter) return items;
    return items.filter((i) => (i.status || "new") === statusFilter);
  }, [items, statusFilter]);

  const selected = items.find((i) => i.id === selectedId);

  const patchLocal = (updated) => {
    setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
  };

  const counts = useMemo(() => {
    const c = { all: items.length, new: 0, in_progress: 0, resolved: 0, archived: 0 };
    items.forEach((i) => { c[i.status || "new"] = (c[i.status || "new"] || 0) + 1; });
    return c;
  }, [items]);

  return (
    <DashLayout>
      <div className="max-w-7xl mx-auto py-8 px-4">
        <div className="flex items-center gap-3 mb-6">
          <Inbox className="text-[color:var(--fl-orange)]" size={22} />
          <h1 className="fl-heading text-3xl">Inquiries</h1>
          <span className="text-sm ml-2" style={{ color: "var(--fl-muted-2)" }}>
            {counts.all} total · {counts.new} new · {counts.in_progress} in progress
          </span>
        </div>

        <div className="flex flex-wrap gap-2 mb-4" data-testid="inq-filter-bar">
          <button onClick={() => setStatusFilter("")} className={`text-xs px-3 py-1.5 rounded border ${!statusFilter ? "border-[color:var(--fl-orange)] text-[color:var(--fl-orange)]" : "border-white/10"}`} data-testid="inq-filter-all">
            All · {counts.all}
          </button>
          {STATUS_ORDER.map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)} className={`text-xs px-3 py-1.5 rounded border ${statusFilter === s ? "border-[color:var(--fl-orange)] text-[color:var(--fl-orange)]" : "border-white/10"}`} data-testid={`inq-filter-${s}`}>
              {STATUS_LABEL[s]} · {counts[s] || 0}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-12 gap-4">
          <div className="md:col-span-4 fl-card overflow-hidden" style={{ maxHeight: "calc(100vh - 240px)", overflowY: "auto" }} data-testid="inq-list">
            {loading ? (
              <div className="p-6 text-sm" style={{ color: "var(--fl-muted)" }}>Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-sm" style={{ color: "var(--fl-muted)" }}>No inquiries in this view.</div>
            ) : (
              filtered.map((i) => (
                <InquiryRow key={i.id} inq={i} selected={i.id === selectedId} onOpen={(x) => { setSelectedId(x.id); setSearchParams({ open: x.id }); }} />
              ))
            )}
          </div>
          <div className="md:col-span-8 fl-card" style={{ minHeight: "60vh" }}>
            {selected ? (
              <InquiryDetail inq={selected} onPatch={patchLocal} />
            ) : (
              <div className="p-10 text-center text-sm flex flex-col items-center gap-3" style={{ color: "var(--fl-muted)" }}>
                <ChevronRight size={24} className="opacity-40" />
                Select an inquiry to view details.
              </div>
            )}
          </div>
        </div>
      </div>
    </DashLayout>
  );
}
